/**
 * GO7 Launch / Standard pricing — synced from Firestore pricingSettings/current
 */
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase.config';

export const PRICING_SETTINGS_DOC_PATH = 'pricingSettings/current';

export const DEFAULT_PRICING_SETTINGS = {
  launchMode: true,
  launchBaseFee: 1000,
  launchIncludedKm: 2,
  launchPerKmAfterIncluded: 150,
  standardBaseFee: 1000,
  standardIncludedKm: 2,
  standardPerKmAfterIncluded: 180,
  waitingGraceMinutes: 10,
  waitingFeePerMinute: 50,
  enableTimeCharge: false,
  minimumDeliveryFee: 1000,
  maximumDeliveryFee: 6000,
  go7BikeRiderDeliveryShare: 0.6,
  defaultRiderDeliveryShare: 0.7,
};

export type PricingSettings = typeof DEFAULT_PRICING_SETTINGS & {
  updatedAt?: unknown;
};

let activeSettings: PricingSettings = { ...DEFAULT_PRICING_SETTINGS };

function toNumber(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function normalizePricingSettings(data: Record<string, unknown> = {}): PricingSettings {
  const d = DEFAULT_PRICING_SETTINGS;
  return {
    launchMode: data.launchMode !== false,
    launchBaseFee: toNumber(data.launchBaseFee, d.launchBaseFee),
    launchIncludedKm: toNumber(data.launchIncludedKm, d.launchIncludedKm),
    launchPerKmAfterIncluded: toNumber(data.launchPerKmAfterIncluded, d.launchPerKmAfterIncluded),
    standardBaseFee: toNumber(data.standardBaseFee, d.standardBaseFee),
    standardIncludedKm: toNumber(data.standardIncludedKm, d.standardIncludedKm),
    standardPerKmAfterIncluded: toNumber(data.standardPerKmAfterIncluded, d.standardPerKmAfterIncluded),
    waitingGraceMinutes: toNumber(data.waitingGraceMinutes, d.waitingGraceMinutes),
    waitingFeePerMinute: toNumber(data.waitingFeePerMinute, d.waitingFeePerMinute),
    enableTimeCharge: Boolean(data.enableTimeCharge),
    minimumDeliveryFee: toNumber(data.minimumDeliveryFee, d.minimumDeliveryFee),
    maximumDeliveryFee: toNumber(data.maximumDeliveryFee, d.maximumDeliveryFee),
    go7BikeRiderDeliveryShare: toNumber(data.go7BikeRiderDeliveryShare, d.go7BikeRiderDeliveryShare),
    defaultRiderDeliveryShare: toNumber(data.defaultRiderDeliveryShare, d.defaultRiderDeliveryShare),
    updatedAt: data.updatedAt ?? null,
  };
}

export function getActivePricingSettings(): PricingSettings {
  return activeSettings;
}

export function setActivePricingSettings(settings: PricingSettings): PricingSettings {
  activeSettings = normalizePricingSettings(settings);
  return activeSettings;
}

export function calculateGo7DeliveryFare(
  settings: PricingSettings,
  options: {
    distanceKm?: number;
    waitingMinutes?: number;
    packageMultiplier?: number;
    durationMin?: number;
  } = {}
) {
  const s = normalizePricingSettings(settings);
  const km = Math.max(0, toNumber(options.distanceKm, 0));
  const waitingMinutes = Math.max(0, toNumber(options.waitingMinutes, 0));
  const packageMultiplier =
    Number.isFinite(options.packageMultiplier) && (options.packageMultiplier ?? 0) > 0
      ? (options.packageMultiplier as number)
      : 1;

  const launch = s.launchMode;
  const baseFee = launch ? s.launchBaseFee : s.standardBaseFee;
  const includedKm = launch ? s.launchIncludedKm : s.standardIncludedKm;
  const perKm = launch ? s.launchPerKmAfterIncluded : s.standardPerKmAfterIncluded;

  const chargeableKm = Math.max(0, Math.round((km - includedKm) * 100) / 100);
  const distanceCharge = Math.round(chargeableKm * perKm);

  let waitingCharge = 0;
  if (waitingMinutes > s.waitingGraceMinutes) {
    waitingCharge = Math.round((waitingMinutes - s.waitingGraceMinutes) * s.waitingFeePerMinute);
  }

  let timeFee = 0;
  if (s.enableTimeCharge && options.durationMin != null) {
    timeFee = Math.round(Math.max(0, toNumber(options.durationMin, 0)) * 20);
  }

  const subtotalBeforeClamp = baseFee + distanceCharge + waitingCharge + timeFee;
  let deliverySubtotal = Math.max(
    s.minimumDeliveryFee,
    Math.min(s.maximumDeliveryFee, subtotalBeforeClamp)
  );

  const packageSurcharge =
    packageMultiplier > 1 ? Math.round(deliverySubtotal * (packageMultiplier - 1)) : 0;
  let total = deliverySubtotal + packageSurcharge;
  total = Math.round(total / 50) * 50;
  total = Math.max(total, s.minimumDeliveryFee);

  return {
    pricingMode: launch ? 'launch' : 'standard',
    baseFee,
    distanceCharge,
    total,
    distance: km,
  };
}

export function getRiderShareRate(
  vehicleType: 'bike' | 'bicycle' | 'leg',
  settings: PricingSettings = activeSettings
): number {
  if (vehicleType === 'bike') return settings.go7BikeRiderDeliveryShare;
  return settings.defaultRiderDeliveryShare;
}

export function calculateRiderEarningsFromDeliveryFee(
  deliveryFee: number,
  vehicleType: 'bike' | 'bicycle' | 'leg',
  settings: PricingSettings = activeSettings
): number {
  const fee = Math.round(Math.max(0, Number(deliveryFee) || 0));
  if (fee <= 0) return 0;
  return Math.round(fee * getRiderShareRate(vehicleType, settings));
}

export async function fetchPricingSettings(): Promise<PricingSettings> {
  const snap = await getDoc(doc(db, 'pricingSettings', 'current'));
  if (!snap.exists()) {
    return setActivePricingSettings(DEFAULT_PRICING_SETTINGS);
  }
  return setActivePricingSettings(normalizePricingSettings(snap.data() as Record<string, unknown>));
}

export function subscribePricingSettings(onChange?: (settings: PricingSettings) => void): () => void {
  return onSnapshot(
    doc(db, 'pricingSettings', 'current'),
    (snap) => {
      const settings = snap.exists()
        ? normalizePricingSettings(snap.data() as Record<string, unknown>)
        : normalizePricingSettings(DEFAULT_PRICING_SETTINGS);
      setActivePricingSettings(settings);
      onChange?.(settings);
    },
    (err) => {
      console.warn('[pricing] Firestore subscribe failed, using defaults:', err?.message);
      const settings = normalizePricingSettings(DEFAULT_PRICING_SETTINGS);
      setActivePricingSettings(settings);
      onChange?.(settings);
    }
  );
}
